import * as React from "react";
import {
  Button,
  Flex,
  Heading,
  Switch,
  Text,
  TextField,
  View,
} from "@adobe/react-spectrum";
import { ContextSelection } from "../../components/protected/Search/ContextSelection/ContextSelection";
import type { ContextObject } from "../../components/protected/Search/ContextSelection/types";
import { usePreloadAssets } from "../../hooks/UsePreloadAssets";

export default function ContextSelectionDemo() {
  const {
    folderStatusMapping,
    projectStatusMapping,
    activityStatusMapping,
  } = usePreloadAssets();

  const [value, setValue] = React.useState<ContextObject | null>(null);
  const [readOnly, setReadOnly] = React.useState(false);
  const [showBorders, setShowBorders] = React.useState(true);
  const [allowTransparentBG, setAllowTransparentBG] = React.useState(false);
  /** Demo defaults to true so the inline clear control is easy to exercise. */
  const [allowClearSelection, setAllowClearSelection] = React.useState(true);
  /** When on, Browse/Results edit mode shows the sticky Add row. */
  const [allowAdd, setAllowAdd] = React.useState(true);
  const [log, setLog] = React.useState<string[]>([]);
  /** Drives GetContextPath inside ContextSelection; clear after load if you only need one shot. */
  const [explicitContextId, setExplicitContextId] = React.useState<
    number | null
  >(null);
  const [explicitIdInput, setExplicitIdInput] = React.useState("");

  const appendLog = React.useCallback((ctx: ContextObject | null) => {
    const ts = new Date().toISOString();
    const line =
      ctx === null
        ? `[${ts}] onSelectionChange: null (cleared)`
        : `[${ts}] onSelectionChange: ${JSON.stringify(ctx)}`;
    setLog((prev) => [line, ...prev].slice(0, 50));
  }, []);

  const appendAddLog = React.useCallback(() => {
    const ts = new Date().toISOString();
    const line = `[${ts}] onAdd`;
    setLog((prev) => [line, ...prev].slice(0, 50));
  }, []);

  const appendExplicitResolveErrorLog = React.useCallback((e: unknown) => {
    const ts = new Date().toISOString();
    const msg = e instanceof Error ? e.message : String(e);
    const line = `[${ts}] onExplicitContextResolveError: ${msg}`;
    setLog((prev) => [line, ...prev].slice(0, 50));
  }, []);

  return (
    <View padding="size-400" maxWidth="800px" marginX="auto">
      <Flex direction="column" gap="size-200">
        <Heading level={1}>Context selection (sandbox)</Heading>
        <Text>
          Internal gallery for the ContextSelection control: search and history
          match Global Search; use the switches and reset to exercise states.
          When &quot;Show clear button&quot; is on, use the X on the field or the
          secondary button below to clear—both emit the same onSelectionChange
          event.           With &quot;Allow add&quot; on, open edit mode, type to search or click
          a path segment to browse; the footer logs onAdd when clicked.
        </Text>

        <Flex direction="column" gap="size-100" alignItems="start">
          <Switch isSelected={showBorders} onChange={setShowBorders}>
            Show borders (collapsed field and search input)
          </Switch>
          <Switch
            isSelected={allowTransparentBG}
            onChange={setAllowTransparentBG}
          >
            Transparent background (collapsed field blends with page)
          </Switch>
          <Switch isSelected={readOnly} onChange={setReadOnly}>
            Read-only (no edit mode)
          </Switch>
          <Switch
            isSelected={allowClearSelection}
            onChange={setAllowClearSelection}
          >
            Show clear button (inline)
          </Switch>
          <Switch isSelected={allowAdd} onChange={setAllowAdd}>
            Allow add (Browse / Results)
          </Switch>
          <Button
            variant="secondary"
            onPress={() => {
              setValue(null);
              setExplicitContextId(null);
              appendLog(null);
            }}
          >
            Clear selection
          </Button>
        </Flex>

        <Flex direction="row" gap="size-100" alignItems="end" wrap>
          <TextField
            label="Load by context id (GetContextPath)"
            width="size-2400"
            value={explicitIdInput}
            onChange={setExplicitIdInput}
            description="Sets explicitContextId; resolved path appears in the field and in the log."
          />
          <Button
            variant="accent"
            onPress={() => {
              const n = parseInt(explicitIdInput.trim(), 10);
              if (!Number.isNaN(n) && n > 0) {
                setExplicitContextId(n);
              }
            }}
          >
            Resolve
          </Button>
          <Button variant="secondary" onPress={() => setExplicitContextId(null)}>
            Clear explicit id
          </Button>
        </Flex>

        <ContextSelection
          value={value}
          onSelectionChange={(ctx) => {
            setValue(ctx);
            appendLog(ctx);
          }}
          folderStatusMap={folderStatusMapping}
          projectStatusMap={projectStatusMapping}
          activityStatusMap={activityStatusMapping}
          isReadOnly={readOnly}
          showBorders={showBorders}
          allowTransparentBG={allowTransparentBG}
          allowClearSelection={allowClearSelection}
          allowAdd={allowAdd}
          onAdd={appendAddLog}
          maxVisibleItems={5}
          aria-label="Demo context selection"
          explicitContextId={explicitContextId}
          onExplicitContextResolveError={appendExplicitResolveErrorLog}
          defaultValue={587190101}
        />

        <View
          borderWidth="thin"
          borderColor="dark"
          borderRadius="medium"
          padding="size-200"
          minHeight="size-2000"
        >
          <Heading level={3}>Live log (onSelectionChange, onAdd)</Heading>
          <View
            marginTop="size-100"
            padding="size-100"
            backgroundColor="gray-100"
            borderRadius="small"
          >
            {log.length === 0 ? (
              <Text>No events yet.</Text>
            ) : (
              <Flex direction="column" gap="size-50">
                {log.map((line, i) => (
                  <Text key={i} UNSAFE_style={{ fontFamily: "monospace", fontSize: 12 }}>
                    {line}
                  </Text>
                ))}
              </Flex>
            )}
          </View>
        </View>
      </Flex>
    </View>
  );
}
